import { useState } from 'react';

import { WEEKDAY_NAMES } from '@dca/shared';

import { ActivityCurve, DemandHeatmap } from '../components/Charts.tsx';
import { DataTable } from '../components/DataTable.tsx';
import { Card, Empty, Notice, Spinner } from '../components/Ui.tsx';
import { api } from '../lib/api.ts';
import { useAsync } from '../lib/auth.tsx';
import { hourLabel, number } from '../lib/format.ts';
import { ErrorState } from './Upgrade.tsx';

export function DemandPage() {
  const [days, setDays] = useState(30);
  const demand = useAsync(() => api.insights.demand(days), [days]);

  if (demand.loading) return <Spinner />;
  if (demand.error) return <ErrorState error={demand.error} />;

  const profile = demand.data!.profile;
  const serverCurve = demand.data!.serverActivity;

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Demand windows</h1>
          <p>
            When your buyers actually turn up, from {number(profile.totalUnits)} units of realized
            sales. Times are shown in server time.
          </p>
        </div>
        <div className="pill-row">
          {[7, 30, 90].map((option) => (
            <button
              key={option}
              className={`pill ${days === option ? 'active' : ''}`}
              onClick={() => setDays(option)}
            >
              {option} days
            </button>
          ))}
        </div>
      </header>

      <Card
        title="Sales by hour and weekday"
        subtitle="Darker means more units sold in that hour"
      >
        {profile.totalUnits === 0 ? (
          <Empty title="No sales in this window yet" />
        ) : (
          <DemandHeatmap cells={profile.cells} weekdayNames={WEEKDAY_NAMES} />
        )}
      </Card>

      <div className="grid two" style={{ marginTop: 16 }}>
        <Card title="Your peak windows" subtitle="Best three-hour block on each day">
          {profile.peakWindows.length === 0 ? (
            <Empty title="Not enough data to call a peak" />
          ) : (
            <DataTable
              rows={profile.peakWindows}
              rowKey={(window) => `${window.weekday}-${window.startHour}`}
              initialSort={{ key: 'units', dir: 'desc' }}
              columns={[
                {
                  key: 'day',
                  header: 'Day',
                  sortValue: (window) => window.weekday,
                  render: (window) => WEEKDAY_NAMES[window.weekday],
                },
                {
                  key: 'window',
                  header: 'Window',
                  sortValue: (window) => window.startHour,
                  render: (window) => `${hourLabel(window.startHour)} - ${hourLabel(window.endHour)}`,
                },
                {
                  key: 'units',
                  header: 'Units',
                  align: 'num',
                  sortValue: (window) => window.units,
                  render: (window) => number(window.units),
                },
                {
                  key: 'share',
                  header: 'Share of week',
                  align: 'num',
                  sortValue: (window) => window.sharePct,
                  render: (window) => `${window.sharePct}%`,
                },
              ]}
            />
          )}
        </Card>

        <Card
          title="Server-wide activity"
          subtitle="Backdrop for comparison, entered by an operator - not scraped"
        >
          {serverCurve.length === 24 ? (
            <ActivityCurve curve={serverCurve} />
          ) : (
            <Empty title="No server activity curve recorded" />
          )}
          <div style={{ marginTop: 12 }}>
            <Notice>
              If your peak sits well before the server peak, you are probably selling to a different
              crowd than the bulk of the player base - worth testing a restock later in the evening.
            </Notice>
          </div>
        </Card>
      </div>

      {profile.quietHours.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Notice>
            <strong>Dead hours:</strong> {profile.quietHours.map(hourLabel).join(', ')} carry under
            1% of your weekly volume each. Repricing during these hours gives you the fastest read
            on whether a change worked, because almost nothing else is moving.
          </Notice>
        </div>
      )}
    </>
  );
}
